import React, { useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { Loading } from 'components/Loading'
import { FilterInput } from 'components/UIParts'
import { filterStates } from 'database/AppApolloProvider'
import options from "contexts/options.json"
import { useMongoQueries } from 'contexts/useMongoQuery'
import { DataTable, TableHeadBase, createColumnsFromHeads } from 'components/DataTable'

const createHeads = <T extends readonly TableHeadBase[]>(configs: T): T => configs

const heads = createHeads([
  { field: "clinic_name", text: "施設名" },
  { field: "postal_code", text: "施設郵便番号" },
  { field: "address1", text: "住所1" },
  { field: "address2", text: "住所2" },
  { field: "tel", text: "施設電話番号" },
  { field: "fax", text: "施設FAX" },
  {
    field: "clinic_type",
    text: "外注",
    cell: (row: KV) => (row.clinic_type ? "" : "◯"),
  },
  {
    field: "order",
    text: "表示順",
    cell: (row: KV) => (row._id === options.centralClinic.id ? "-" : (row.order ?? "")),
  },
])

const getQueryVariables = (names: KV): KV => {
  return Object.keys(names).reduce<KV>((vars, key) => (names[key] ? { ...vars, [key]: names[key] } : vars), {})
}

export const List = () => {
  const navigate = useNavigate()
  const values: KV = useReactiveVar(filterStates)
  const names = values.clinic || { clinic_name: "", department: "", doctor_name: "", name: "" }
  const setValue = (name: string, value: string) => filterStates({ ...filterStates(), clinic: { ...names, [name]: value } })
  const pipeline = [
    { $match: getQueryVariables(names) },
    {
      $lookup: {
        from: "bookings",
        let: { clinicIdStr: { $toString: "$_id" } },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ["$clinicID", "$$clinicIdStr"] },
                  { $eq: ["$bookingType", 1] }
                ]
              }
            }
          }
        ],
        as: "matchingBookings"
      }
    },
    {
      $addFields: {
        bookingCount: { $size: "$matchingBookings" }
      }
    },
    {
      $sort: { bookingCount: -1 }
    },
    {
      $limit: 200
    }
  ];
  const { loading, data } = useMongoQueries("clinics", pipeline)
  const rows: KV[] = data || []
  const columns = useMemo(() => createColumnsFromHeads(heads), [])

  const handleRowClick = (row: KV) => {
    navigate(`/clinic/edit/${row._id}`)
  }

  if (loading) return <div className="w-full h-full flex justify-center items-center"><Loading /></div>

  const filterContent = (
    <>
      <FilterInput label="施設名" name="clinic_name" value={names.clinic_name} setValue={setValue} />
    </>
  )

  return (
    <DataTable<KV>
      data={rows}
      columns={columns}
      filterContent={filterContent}
      itemCount={rows.length}
      onAdd={() => navigate('/clinic/create')}
      getRowProps={row => ({
        key: (row.original as KV)._id,
        'data-id': (row.original as KV)._id,
        className: 'text-sm text-gray-900 hover:bg-theme-50 cursor-pointer',
        onClick: () => handleRowClick(row.original as KV),
      })}
      storageKey="clinicList_columnSettings"
    />
  )
}

export default List